// TODO - Type 'any' needs to be fixed.
import {addDays} from "date-fns";
import endOfWeek from "date-fns/endOfWeek";
import startOfWeek from "date-fns/startOfWeek";
import { Dispatch, SetStateAction, useContext, useEffect, useReducer, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";

import {
  FILTER_JOB_KEY,
  FILTER_LOCATION_KEY, FILTER_MANAGER_KEY,
  FILTER_OVERTIME_AVAILABILITY_KEY,
  FILTER_SHIFT_BY_KEY, FILTER_SHIFT_PATTERN_KEY,
  FILTER_SHIFT_STATUS_KEY,
  FILTER_SKILL_KEY,
  FILTER_WORK_CENTERS_KEY
} from "../../../assets/constants";
import { CompanyUser, WorkCenter } from "../../../utils/types";
import {
	JOB_ROLES,
	LOCATIONS,
	OVERTIME_AVAILABILITY,
	PUBLISHED_BY,
	SHIFT_BY,
	SKILLS,
	STATUS,
	WORK_CENTERS,
} from "../../filters/constants";
import { Filter } from "../../filters/filter-accordion/types";
import { AppliedFilter, DataRelation } from "../../filters/filter-context";
import { resetMaxWorkersByLocationJob } from "../../jobs/store/jobs-slice";
import { jobReducer, skillReducer } from "../reducer";
import {
	changeWeekNoCache,
	fetchFilterAndShiftData,
	fetchSchedulerSettings,
	// fetchInitialData,
	resetAssignWorkers,
	resetBroadcastMessageState,
	resetCreateDraftShiftAsyncState,
	resetDeleteDraftShiftAsync,
	resetRenotifyWorkers,
} from "../store/scheduler-slice";
import {
	Job,
	// Shift,
	SkillData,
} from "../store/types";
import { getDayNumber } from "../ui/dialog/copy-shifts-dialog/utils";
import useSelectorData from "./use-scheduler-data";
import useSchedulerDialog from "./use-scheduler-dialog";

const UseSchedulerController = ({
  filters,
  setFilters,
  setDataRelations,
}: {
  filters: Filter[];
  setFilters: (newFilters: Filter[]) => void;
  setDataRelations: Dispatch<SetStateAction<DataRelation[]>>;
}) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const [state, dispatchLocal] = useReducer(jobReducer, {});
  const [skillState, dispatchLocalSkill] = useReducer(skillReducer, {});
  const [defaultAppliedFilters, setDefaultAppliedFilters] = useState<
    AppliedFilter[]
  >([]);
  const {
    shiftsData,
    filterAllLocations,
    filterAllWorkcenters,
    filterJobRoles,
    filterAllSkills,
    filterAllManagers,
    filterRelationships,
    fetchFilterAndShiftDataStatus,
    schedulerSettingsStatus,
    weekStartDay,
    isWeekView,
    currentDate,
    shiftPatternsWithDates,
  } = useSelectorData();
  const [
    {
      isShiftDetailsDialogOpen,
      isCreateNewShiftDialogOpen,
      isCreateWeekShiftDialogOpen,
      isShiftAttendanceDialogOpen,
      shiftIdForShiftDetailsDialog,
      isPastShiftDialogOpen,
      isMarkAttendanceDialogOpen,
      isShiftCancelDialogOpen,
      isDeleteDraftShiftDialogOpen,
      isPublishDraftShiftDialogOpen,
    },
    {
      setIsCreateNewShiftDialogOpen,
      setIsCreateWeekShiftDialogOpen,
      setIsShiftDetailsDialogOpen,
      setIsShiftAttendanceDialogOpen,
      setShiftIdForShiftDetailsDialog,
      setIsPastShiftDialogOpen,
      setIsMarkAttendanceDialogOpen,
      setIsShiftCancelDialogOpen,
      setIsDeleteDraftShiftDialogOpen,
      setIsPublishDraftShiftDialogOpen,
    },
  ]: any = useSchedulerDialog();

  /**
   * Initial state
   */
  const [isInitSet, setIsInitSet] = useState(false);
  const [isSkillInitSet, setIsSkillInitSet] = useState(false);
  const [weekPasteDate, setWeekPasteDate] = useState<any>(null);
  const [draftShiftDate, setDraftShiftDate] = useState<any>(null);

  useEffect(() => {
    if (fetchFilterAndShiftDataStatus === "fulfilled") {
      const newFilters: Filter[] = [];
      if (filterAllLocations && filterAllLocations.length > 0) {
        newFilters.push({
          id: 1,
          name: LOCATIONS,
          key: FILTER_LOCATION_KEY,
          data: filterAllLocations,
          reducedData: filterAllLocations,
          searchedData: filterAllLocations,
        });
      }
      if (filterAllWorkcenters && filterAllWorkcenters.length > 0) {
        newFilters.push({
          id: 2,
          name: WORK_CENTERS,
          key: FILTER_WORK_CENTERS_KEY,
          data: filterAllWorkcenters,
          reducedData: filterAllWorkcenters,
          searchedData: filterAllWorkcenters,
        });
      }
      if (filterJobRoles && filterJobRoles.length > 0) {
        newFilters.push({
          id: 3,
          name: JOB_ROLES,
          key: FILTER_JOB_KEY,
          data: filterJobRoles,
          reducedData: filterJobRoles,
          searchedData: filterJobRoles,
        });
      }
      if (filterAllSkills && filterAllSkills.length > 0) {
        newFilters.push({
          id: 4,
          name: SKILLS,
          key: FILTER_SKILL_KEY,
          data: filterAllSkills,
          reducedData: filterAllSkills,
          searchedData: filterAllSkills,
        });
      }
      // Shift status
      // `labelKey` carries the language-neutral translation key so the value stays
      // translatable everywhere (applied chip, hover popup) even after it is
      // selected and persisted; `name` is the current-language string for the
      // checkbox list, rebuilt whenever the language changes (t is a dep below).
      const shiftStatusOptions = [
        { id: 1, name: t("scheduler_draft"), labelKey: "scheduler_draft" },
        { id: 2, name: t("scheduler_published"), labelKey: "scheduler_published" },
      ];
      newFilters.push({
        id: 5,
        name: STATUS,
        key: FILTER_SHIFT_STATUS_KEY,
        data: [...shiftStatusOptions],
        reducedData: [...shiftStatusOptions],
        searchedData: [...shiftStatusOptions],
      });
      // Shift By Job or Skills
      const shiftByOptions = [
        { id: 1, name: t("scheduler_job"), labelKey: "scheduler_job" },
        { id: 2, name: t("scheduler_skills"), labelKey: "scheduler_skills" },
      ];
      newFilters.push({
        id: 6,
        name: SHIFT_BY,
        key: FILTER_SHIFT_BY_KEY,
        data: [...shiftByOptions],
        reducedData: [...shiftByOptions],
        searchedData: [...shiftByOptions],
      });

      const filterUsersByWorkCenters = (
        users: CompanyUser[],
        workCenters: WorkCenter[]
      ): CompanyUser[] => {
        const workCenterIds = new Set(workCenters.map((wc) => wc.id));

        // TODO: API not returning workcenter or location of user, update when data is available via API
        // Do not filter if userWorkCenters do not exists
        return users.filter((user) =>
          user.userWorkCenters
            ? user.userWorkCenters.some((wc) => workCenterIds.has(wc.id))
            : true
        );
      };

      const managers = filterUsersByWorkCenters(
        filterAllManagers,
        filterAllWorkcenters
      );
      // Shift created by
      newFilters.push({
        id: 7,
        name: PUBLISHED_BY,
        key: FILTER_MANAGER_KEY,
        data: managers,
        reducedData: managers,
        searchedData: managers,
      });
      if (shiftPatternsWithDates && shiftPatternsWithDates.length > 0) {
        newFilters.push({
          id: 8,
          name: OVERTIME_AVAILABILITY,
          key: FILTER_OVERTIME_AVAILABILITY_KEY,
          data: shiftPatternsWithDates,
          reducedData: shiftPatternsWithDates,
          searchedData: shiftPatternsWithDates,
        });
      }
      setFilters(newFilters);
      // Clone DataRelations and extend for shift equivalents
      let relations: DataRelation[] = filterRelationships ?? [];

      const workCenterToShiftPatterns = relations.find(
        (r) =>
          r.from === FILTER_WORK_CENTERS_KEY &&
          r.to === FILTER_SHIFT_PATTERN_KEY
      );

      if (workCenterToShiftPatterns) {
        const shiftPatternRelations: DataRelation = {
          from: FILTER_WORK_CENTERS_KEY,
          to: FILTER_OVERTIME_AVAILABILITY_KEY,
          mappings: workCenterToShiftPatterns.mappings,
        };
        relations = [...relations, shiftPatternRelations];
      }
      setDataRelations(relations);
    }
  }, [
    fetchFilterAndShiftDataStatus,
    filterAllLocations,
    filterAllWorkcenters,
    filterJobRoles,
    filterAllManagers,
    filterAllSkills,
    shiftPatternsWithDates,
    filterRelationships,
    setFilters,
    setDataRelations,
    t,
  ]);

  useEffect(() => {
    if (filterAllSkills?.length > 0) {
      const cb: any = {};
      filterAllSkills.forEach((skill: SkillData) => {
        cb[skill.id] = false;
      });
      if (!isSkillInitSet) {
        dispatchLocalSkill({ type: "setInit", payload: cb });
        setIsSkillInitSet(true);
      }
    }
  }, [filterAllSkills, isSkillInitSet]);

  useEffect(() => {
    if (filterJobRoles?.length > 0) {
      const cb: any = {};
      filterJobRoles.forEach((job: Job) => {
        cb[job.id] = false;
      });
      if (!isInitSet) {
        dispatchLocal({ type: "setInit", payload: cb });
        setIsInitSet(true);
      }
    }
  }, [filterJobRoles, isInitSet]);

  useEffect(() => {
    try {
      if (isWeekView && currentDate && weekStartDay && dispatch) {
        dispatch(changeWeekNoCache(new Date(currentDate)));
      }
    } catch (e) {
      // Do nothing
    }
    // Note: Not including several required dependencies to avoid multiple API calls
    // Intention of this useEffect is only to refresh API when we switch to week view from day view
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isWeekView]);

  useEffect(() => {
    if (
      dispatch &&
      fetchFilterAndShiftDataStatus === "idle" &&
      (schedulerSettingsStatus === "fulfilled" ||
        schedulerSettingsStatus === "error")
    ) {
      dispatch(fetchFilterAndShiftData());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [schedulerSettingsStatus]);

  useEffect(() => {
    if (dispatch && schedulerSettingsStatus === "idle") {
      dispatch(fetchSchedulerSettings());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // useEffect(() => {
  // 	if (dispatch && fetchFilterAndShiftDataStatus === "fulfilled") {
  // 		dispatch(fetchInitialData());
  // 	}
  // 	// eslint-disable-next-line react-hooks/exhaustive-deps
  // },[fetchFilterAndShiftDataStatus]);

  /**
   * Toggle functions for the Create New Shift modal
   * */
  const openCreateNewShiftDialog = (date: any) => {
    setDraftShiftDate(date);
    dispatch(resetCreateDraftShiftAsyncState());
    dispatch(resetMaxWorkersByLocationJob());
    setIsCreateNewShiftDialogOpen(true);
  };

  const closeCreateNewShiftDialog = () => {
    setIsCreateNewShiftDialogOpen(false);
  };

  /**
   *  Toggle functions for the Create new week shift modal
   */
  const openCreateWeekShiftDialog = (date: any) => {
    const start = addDays(
      startOfWeek(date, { weekStartsOn: getDayNumber(weekStartDay) }),
      0
    );
    const end = addDays(
      endOfWeek(date, { weekStartsOn: getDayNumber(weekStartDay) }),
      0
    );
    setDraftShiftDate(start);
    setWeekPasteDate({ startDate: start, endDate: end });
    setIsCreateWeekShiftDialogOpen(true);
  };

  const closeCreateWeekShiftDialog = () => {
    setIsCreateWeekShiftDialogOpen(false);
  };

  const openPastShiftDialog = () => {
    setIsPastShiftDialogOpen(true);
  };

  const closePastShiftDialog = () => {
    setIsPastShiftDialogOpen(false);
  };

  const openMarkAttendanceDialog = () => {
    setIsMarkAttendanceDialogOpen(true);
  };

  const closeMarkAttendanceDialog = () => {
    setIsMarkAttendanceDialogOpen(false);
  };

  const openShiftCancelDialog = () => {
    setIsShiftCancelDialogOpen(true);
  };

  const closeShiftCancelDialog = () => {
    setIsShiftCancelDialogOpen(false);
  };

  const openDeleteDraftShiftDialog = () => {
    setIsDeleteDraftShiftDialogOpen(true);
  };

  const closeDeleteDraftShiftDialog = () => {
    setIsDeleteDraftShiftDialogOpen(false);
  };

  const openPublishDraftShiftDialog = () => {
    setIsPublishDraftShiftDialogOpen(true);
  };

  const closePublishDraftShiftDialog = () => {
    setShiftIdForShiftDetailsDialog(null);
    setIsPublishDraftShiftDialogOpen(false);
  };

  return [
    {
      isShiftDetailsDialogOpen,
      isShiftAttendanceDialogOpen,
      isCreateWeekShiftDialogOpen,
      shiftIdForShiftDetailsDialog,
      isCreateNewShiftDialogOpen,
      isPastShiftDialogOpen,
      isMarkAttendanceDialogOpen,
      weekPasteDate,
      draftShiftDate,
      state,
      skillState,
      isInitSet,
      isSkillInitSet,
      filters,
      defaultAppliedFilters,
      isShiftCancelDialogOpen,
      isDeleteDraftShiftDialogOpen,
      isPublishDraftShiftDialogOpen,
      shiftsData,
    },
    {
      closeCreateWeekShiftDialog,
      openCreateWeekShiftDialog,
      openPastShiftDialog,
      closePastShiftDialog,
      openCreateNewShiftDialog,
      openDeleteDraftShiftDialog,
      closeDeleteDraftShiftDialog,
      openMarkAttendanceDialog,
      closeMarkAttendanceDialog,
      setShiftIdForShiftDetailsDialog,
      setIsShiftDetailsDialogOpen,
      setIsShiftAttendanceDialogOpen,
      closeCreateNewShiftDialog,
      resetDeleteDraftShiftAsync,
      resetBroadcastMessageState,
      resetRenotifyWorkers,
      resetAssignWorkers,
      dispatchLocal,
      dispatchLocalSkill,
      setFilters,
      openShiftCancelDialog,
      closeShiftCancelDialog,
      openPublishDraftShiftDialog,
      closePublishDraftShiftDialog,
    },
  ];
};

export default UseSchedulerController;
